<template>
  <LoadingCircle v-if="isLoading" />
  <div v-else>
    <v-row no-gutters>
      <v-col>
        <StandardCardContainer>
          <template #title>
            <div class="d-flex justify-space-between align-center">
              {{ selectedQuestionnaireStore.questionnaire.questionnaireLabel }}
              <p>
                <AnswerStatusText
                  class="justify-end"
                  :status="
                    selectedQuestionnaireStore.questionnaire.answerStatus
                  "
                />
              </p>
            </div>
            <p class="text-left ml-1">
              Last edited
              {{ selectedQuestionnaireStore.questionnaire.lastEdited }}
            </p>
          </template>
          <div class="info-table mt-8 mb-2">
            <v-row class="text-left" no-gutters>
              <v-col class="divider py-1 pl-2" cols="12" md="6" lg="3">
                <p class="header">Owner</p>
                <p>{{ selectedQuestionnaireStore.questionnaire.tlcName }}</p>
              </v-col>
              <v-col class="divider py-1 pl-2" cols="12" md="6" lg="3">
                <p class="header">
                  {{
                    selectedQuestionnaireStore.questionnaire.senderCompanies
                      .length > 1
                      ? "Senders"
                      : "Sender"
                  }}
                </p>
                <p>
                  {{
                    stringifySuppliers(
                      selectedQuestionnaireStore.questionnaire.senderCompanies
                    )
                  }}
                </p>
              </v-col>
              <v-col class="divider py-1 pl-2 text-left" cols="12" lg="4">
                <p class="header">
                  {{
                    selectedQuestionnaireStore.questionnaire.senderEmails
                      .length > 1
                      ? "Sender emails"
                      : "Sender email"
                  }}
                </p>
                <p>
                  {{
                    stringifySuppliers(
                      selectedQuestionnaireStore.questionnaire.senderEmails
                    )
                  }}
                </p>
              </v-col>
              <v-col class="text-right" align-self="end" cols="12" lg="2">
                <PPrimaryButtonVue @click="routeToAnswering">
                  {{
                    selectedQuestionnaireStore.questionnaire.answerStatus ===
                    AnswerStatus.Completed
                      ? "View answers"
                      : "Answer"
                  }}
                </PPrimaryButtonVue>
              </v-col>
            </v-row>
          </div>
        </StandardCardContainer>
      </v-col>
    </v-row>
    <v-row v-if="!selectedQuestionnaireStore.questionnaire.isBusinessPartner">
      <v-col cols="12">
        <StandardCardContainer>
          <template #title>
            <div class="d-flex justify-space-between align-center">
              Forwarded to
              <PSecondaryButton
                v-if="forwardedQuestionnaires.length > 0"
                appkit-icon="icon-plus-fill"
                @click="forwardQuestionnaire"
              >
                Add recipients
              </PSecondaryButton>
            </div>
          </template>
          <v-col cols="12">
            <o-table
              v-if="forwardedQuestionnaires.length > 0"
              class="ma-2"
              :data="tableData"
              :paginated="true"
              :per-page="100"
            >
              <o-table-column v-for="column in columns" v-bind="column">
                <template #default="props">
                  <AnswerStatusText
                    v-if="column.field === 'answerStatus'"
                    :status="props.row[column.field]"
                  >
                  </AnswerStatusText>
                  <p v-else>{{ props.row[column.field] }}</p>
                </template>
              </o-table-column>
            </o-table>
            <div v-else class="my-8 text-center">
              <p>The questionnaire has not been forwarded to any suppliers.</p>
              <PSecondaryButton @click="forwardQuestionnaire" class="mt-4">
                Forward
              </PSecondaryButton>
            </div>
          </v-col>
        </StandardCardContainer>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { RouterPath } from "@/models/enum/routerPath";
import PPrimaryButtonVue from "@/components/custom/buttons/PPrimaryButton.vue";
import AnswerStatusText from "@/components/questionnaire/AnswerStatusText.vue";
import { useRouter } from "vue-router";
import { ref, onBeforeMount, computed, type Ref } from "vue";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import { useUserProfileStore } from "@/stores/userProfileStore";
import { AnswerStatus } from "@/models/enum/answerStatus";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { getForwardedQuestionnaires } from "@/services/questionnaireService";
import type { ForwardedQuestionnaireDto } from "@/models/dto/forwardedQuestionnaire";
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import PSecondaryButton from "@/components/custom/buttons/PSecondaryButton.vue";
import { useSuppliersStore } from "@/stores/suppliersStore";
import { getCompletedReceivedQuestionnairesForTemplateId } from "@/services/questionnairesService";

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const router = useRouter();
const suppliersStore = useSuppliersStore();
const isLoading = ref(false);
const forwardedQuestionnaires: Ref<ForwardedQuestionnaireDto[]> = ref([]);
const hasRecipients = computed(() => forwardedQuestionnaires.value.length > 0);

const userProfileStore = useUserProfileStore();
const snackBarStore = useSnackBarStore();

function stringifySuppliers(supplierList: string[]): string {
  return supplierList.join(",\n");
}

onBeforeMount(async () => {
  isLoading.value = true;
  await getAnswerStatuses();
  isLoading.value = false;
});

const routeToStartQuestionnaire = () => router.push(RouterPath.StartQuestionnaire);
const routeToReuseAnswers = () => router.push(RouterPath.ReuseAnswers);

function forwardQuestionnaire() {
  if (!hasRecipients.value && !suppliersStore.hasSuppliers) {
    router.push(RouterPath.UploadSuppliers);
  } else {
    router.push(RouterPath.SendQuestionnaire);
  }
}

async function getAnswerStatuses(): Promise<void> {
  try {
    forwardedQuestionnaires.value = await getForwardedQuestionnaires(
      selectedQuestionnaireStore.questionnaire.questionnaireId,
      userProfileStore.trustGroup.id
    );
  } catch (error: any) {
    snackBarStore.showError();
  }
}

function getDateString(sendDate: Date | null): string {
  if (sendDate) {
    let date = new Date(sendDate);
    return `${date.getDate().toString().padStart(2, "0")}.${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${date.getFullYear()}`;
  } else {
    return "N/A";
  }
}

const tableData = computed(() =>
  forwardedQuestionnaires.value.map((f) => ({
    companyName: f.companyName,
    answerStatus: f.answerStatus,
    dateSent: getDateString(f.dateSent),
  }))
);

const columns = ref([
  {
    field: "companyName",
    label: "Company",
    sortable: true,
  },
  {
    field: "answerStatus",
    label: "Status",
    sortable: true,
  },
  {
    field: "dateSent",
    label: "Date sent",
    sortable: true,
  },
]);

async function routeToAnswering() {
  const completedAnswers =
    await getCompletedReceivedQuestionnairesForTemplateId(
      selectedQuestionnaireStore.questionnaire.templateId
    );

  selectedQuestionnaireStore.questionnaire.answerStatus ===
    AnswerStatus.NotStarted && completedAnswers.length > 0
    ? routeToReuseAnswers()
    : routeToStartQuestionnaire();
}
</script>

<style scoped lang="scss">
.info-table {
  .header {
    color: var(--grey);
  }
  .divider {
    border-left: 1px solid var(--light-grey-1);
  }
}

.info-card {
  border-radius: 12px;
  border-width: 1px;
}
</style>
